import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/lib/auth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CanonicalUrl } from "@/components/CanonicalUrl";
import { LazyToasters } from "@/components/LazyToasters";
import { MainLayout } from "@/components/layout/MainLayout";
import { RequireAuthLayout } from "@/components/layout/RequireAuthLayout";

// Lazy load all page components for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Listings = lazy(() => import("./pages/Listings"));
const Offers = lazy(() => import("./pages/Offers"));
const MyAllocations = lazy(() => import("./pages/MyAllocations"));
const Profile = lazy(() => import("./pages/Profile"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Legal = lazy(() => import("./pages/Legal"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminResources = lazy(() => import("./pages/admin/Resources"));
const AdminResourceTypes = lazy(() => import("./pages/admin/ResourceTypes"));
const AdminListings = lazy(() => import("./pages/admin/AdminListings"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const PlatformDashboard = lazy(() => import("./pages/platform/Dashboard"));
const PlatformOrganizations = lazy(() => import("./pages/platform/Organizations"));
const CreateOrganization = lazy(() => import("./pages/platform/CreateOrganization"));
const OrganizationDetail = lazy(() => import("./pages/platform/OrganizationDetail"));
const PlatformSuperAdmins = lazy(() => import("./pages/platform/SuperAdmins"));
const BillingPlans = lazy(() => import("./pages/platform/BillingPlans"));
const SupportConsole = lazy(() => import("./pages/platform/SupportConsole"));
const AccountProfile = lazy(() => import("./pages/AccountProfile"));
const NoOrganization = lazy(() => import("./pages/NoOrganization"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        let status: number | undefined;
        if (error && typeof error === "object") {
          status =
            (error as any)?.status ||
            (error as any)?.context?.response?.status;
          if (!status && error instanceof Error) {
            const msg = error.message.toLowerCase();
            if (msg.includes("401") || msg.includes("unauthorized")) status = 401;
            else if (msg.includes("403") || msg.includes("forbidden")) status = 403;
            else if (msg.includes("429")) status = 429;
            else if (msg.includes("500") || msg.includes("503")) status = 500;
          }
        }
        if (status === 401 || status === 403) return false;
        if (status === 429 || (status && status >= 500 && status < 600)) return failureCount < 2;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
    },
    mutations: {
      retry: false,
    },
  },
});

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

/**
 * PERFORMANCE NOTE: TooltipProvider is moved to AuthenticatedLayout (inside ProtectedRoute)
 * This defers loading @radix-ui/react-tooltip from the initial bundle since /auth has no tooltips.
 * 
 * If tooltips are needed on public routes in the future, wrap them locally:
 * <TooltipProvider><YourComponent /></TooltipProvider>
 * 
 * LAYOUT ROUTE: MainLayout is mounted once as a layout route so the header/sidebar
 * persist across navigation. Pages render inside MainLayout's <Outlet />.
 * Profile and NoOrganization are standalone (they handle their own layout for evicted users).
 */
const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LazyToasters />
        <BrowserRouter>
          {/* Dynamic canonical URL - updates on every route change */}
          <CanonicalUrl />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes - these do NOT require authentication */}
              {/* NOTE: No TooltipProvider here - see AuthenticatedLayout if tooltips needed */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/legal" element={<Legal />} />
              
              {/*
                AUTH GATE + LAYOUT ROUTE:
                RequireAuthLayout prevents MainLayout from mounting for unauthenticated users
                (eliminates shell flash on /auth). MainLayout persists across all sidebar-navigable
                routes to avoid header/sidebar remount (logo blink) on navigation.
                ProtectedRoute is still applied per-route for role/org checks.
              */}
              <Route element={<RequireAuthLayout />}>
              <Route element={<MainLayout />}>
                {/* User routes */}
                <Route path="/" element={<ProtectedRoute><Listings /></ProtectedRoute>} />
                <Route path="/offers" element={<ProtectedRoute><Offers /></ProtectedRoute>} />
                <Route path="/allocations" element={<ProtectedRoute><MyAllocations /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                {/* Admin routes - require org-specific admin */}
                <Route path="/admin" element={<ProtectedRoute requireOrgAdmin><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/resources" element={<ProtectedRoute requireOrgAdmin><AdminResources /></ProtectedRoute>} />
                <Route path="/admin/resource-types" element={<ProtectedRoute requireOrgAdmin><AdminResourceTypes /></ProtectedRoute>} />
                <Route path="/admin/listings" element={<ProtectedRoute requireOrgAdmin><AdminListings /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute requireOrgAdmin><AdminUsers /></ProtectedRoute>} />
                <Route path="/admin/settings" element={<ProtectedRoute requireOrgAdmin><AdminSettings /></ProtectedRoute>} />
              </Route>
              </Route>
              
              {/*
                STANDALONE ROUTES: These use their own layout wrappers.
                AccountProfile uses AccountOnlyLayout for evicted users (redirected from /profile by Gate 5).
                NoOrganization uses AccountOnlyLayout.
              */}
              <Route path="/account/profile" element={<ProtectedRoute><AccountProfile /></ProtectedRoute>} />
              <Route path="/no-organization" element={<ProtectedRoute><NoOrganization /></ProtectedRoute>} />
              
              {/* Platform admin routes (Super Admin only) - use PlatformLayout */}
              <Route path="/platform" element={<ProtectedRoute requireSuperAdmin><PlatformDashboard /></ProtectedRoute>} />
              <Route path="/platform/orgs" element={<ProtectedRoute requireSuperAdmin><PlatformOrganizations /></ProtectedRoute>} />
              <Route path="/platform/orgs/new" element={<ProtectedRoute requireSuperAdmin><CreateOrganization /></ProtectedRoute>} />
              <Route path="/platform/orgs/:id" element={<ProtectedRoute requireSuperAdmin><OrganizationDetail /></ProtectedRoute>} />
              <Route path="/platform/admins" element={<ProtectedRoute requireSuperAdmin><PlatformSuperAdmins /></ProtectedRoute>} />
              <Route path="/platform/plans" element={<ProtectedRoute requireSuperAdmin><ErrorBoundary><BillingPlans /></ErrorBoundary></ProtectedRoute>} />
              <Route path="/platform/support" element={<ProtectedRoute requireSuperAdmin><SupportConsole /></ProtectedRoute>} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
